import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowUpRight, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { client } from '@/lib/api';
import EmailMessage from '@/components/EmailMessage';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatInstant } from '@/lib/date';
import { gmailThreadUrl } from '@/lib/gmail-url';

/** One row of the list, as `GET /api/weddings/:id/inbox` sends it. */
interface InboxThread {
  gmailThreadId: string;
  subject: string | null;
  snippet: string | null;
  messageCount: number;
  lastMessageAt: string;
  /** The vendor's side of the conversation, never the planner's. */
  correspondent: string | null;
  inquiry: { id: string; category: string; vendorName: string | null } | null;
}

/**
 * The mail filed under a wedding's Gmail label (EVE-57).
 *
 * The only list in the app that is not read out of the `messages` mirror, and
 * it exists for what the mirror cannot hold: `sync/matcher.ts` writes rows only
 * for mail it can tie to a known vendor, so a thread the planner filed under
 * the label by hand is in Gmail and in none of our tables, and is invisible on
 * every other screen. Showing that thread is the whole feature.
 *
 * Which is why the two kinds of row do different things. A matched thread is a
 * link to `/inquiries/:id` — the quote, the status and the reply box are over
 * there, and a copy of the conversation here would be a dead end with the same
 * text in it. An unmatched thread has nowhere to go, so it opens in place,
 * read-only.
 */
export default function WeddingInboxPanel({
  weddingId,
  gmailAddress,
}: {
  weddingId: string;
  /** The connected mailbox, for the links out to Gmail. Null when none is
   *  connected — the row then renders without the link rather than with a dead
   *  one, since `authuser` can only resolve an address the browser is signed
   *  into. */
  gmailAddress: string | null;
}) {
  // One thread open at a time, as on the inquiry page: these are long reads,
  // and a stack of them all open is a page nobody can scan.
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['wedding-inbox', weddingId],
      // Undefined, never '': `client.ts` omits a param only when it is
      // undefined, and the route refuses an empty token precisely because
      // Gmail would otherwise read it as a real cursor and 400.
      initialPageParam: undefined as string | undefined,
      queryFn: async ({ pageParam }) => {
        const res = await client.api.weddings[':id'].inbox.$get({
          param: { id: weddingId },
          query: { pageToken: pageParam },
        });
        if (!res.ok) throw new Error("This wedding's mail couldn't be loaded from Gmail.");
        return res.json();
      },
      getNextPageParam: (lastPage) =>
        lastPage.state === 'ok' ? (lastPage.nextPageToken ?? undefined) : undefined,
      // Live Gmail reads. Refetching this on every tab focus spends quota to
      // redisplay a list the planner is already looking at.
      staleTime: 60_000,
    });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading this wedding's mail…</p>;
  }

  // Only when there is nothing on screen. A later page failing is reported
  // beside the control that asked for it (below) rather than here, because
  // replacing a list the planner is reading with an error about the page after
  // it loses the threads they came for.
  if (isError && !data) {
    return (
      <Note>
        This wedding's mail couldn't be loaded from Gmail.{' '}
        <button type="button" onClick={() => refetch()} className="underline underline-offset-2">
          Try again
        </button>
      </Note>
    );
  }

  // Whether Gmail can be read at all is a fact about the account, so it is
  // settled by the first page and cannot change underneath a later one.
  const first = data?.pages[0];
  if (!first) return null;

  if (first.state === 'not-connected') {
    // Terse on purpose: GmailBanner is global, already polling for exactly
    // this, and already carrying the link to Settings. Repeating its
    // instructions here would make one gap read as two chores.
    return <Note>Gmail isn't connected, so there's no mail to read here yet.</Note>;
  }

  if (first.state === 'no-read-scope') {
    // The banner says nothing about scope — it reports a connection, and this
    // one is connected — so this is the one state that has to carry its own
    // remedy. And not the connect button: that one was already pressed.
    return (
      <Note>
        Your Gmail connection can send mail but not read it, so this wedding's conversations
        can't be fetched. Disconnect Gmail in{' '}
        <Link to="/settings" className="underline underline-offset-2">
          Settings
        </Link>{' '}
        and connect it again, granting read access.
      </Note>
    );
  }

  const threads = data!.pages.flatMap((page) => (page.state === 'ok' ? page.threads : []));

  if (first.labelMissing) {
    // No label is not an empty label. Telling a planner who has never sent an
    // inquiry that "nothing is filed here" describes a filing that does not
    // exist, and leaves them looking for mail that was never going to arrive.
    return (
      <Note>
        Gmail has no label for this wedding yet. It's created with the first inquiry you send from
        here, and everything filed under it shows up on this tab.
      </Note>
    );
  }

  if (threads.length === 0) {
    return (
      <Note>
        Nothing is filed under this wedding's label yet. Replies to your inquiries land here, and
        so does any thread you file under the label yourself.
      </Note>
    );
  }

  return (
    // A stack of separate cards rather than one card of rows: two threads are
    // two conversations, and a shared surface with hairlines between them reads
    // as one long email.
    <div className="space-y-3">
      {threads.map((thread) => (
        <ThreadRow
          key={thread.gmailThreadId}
          weddingId={weddingId}
          thread={thread}
          expanded={thread.gmailThreadId === expandedThreadId}
          onToggle={() =>
            setExpandedThreadId((current) =>
              current === thread.gmailThreadId ? null : thread.gmailThreadId,
            )
          }
          gmailAddress={gmailAddress}
        />
      ))}

      {/* A page that failed leaves its token in place, so the same control is
          the retry. */}
      {isError && (
        <p role="alert" className="pt-1 text-center text-xs text-destructive">
          The next page of mail couldn't be loaded from Gmail.
        </p>
      )}

      {hasNextPage && (
        <div className="flex justify-center pt-1">
          <Button
            variant="outline"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  );
}

/** The shared classes of the row's leading control — a link on one kind of row,
 *  a disclosure on the other, and the same target either way. */
const HEADER_CONTROL =
  'group flex min-w-0 flex-1 items-start gap-2 py-4 pl-5 pr-2 text-left transition-colors hover:bg-[var(--color-surface-container-low)] focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50 focus-visible:outline-none';

function ThreadRow({
  weddingId,
  thread,
  expanded,
  onToggle,
  gmailAddress,
}: {
  weddingId: string;
  thread: InboxThread;
  expanded: boolean;
  onToggle: () => void;
  gmailAddress: string | null;
}) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['wedding-inbox-thread', weddingId, thread.gmailThreadId],
    queryFn: async () => {
      const res = await client.api.weddings[':id'].inbox.threads[':threadId'].$get({
        param: { id: weddingId, threadId: thread.gmailThreadId },
      });
      if (!res.ok) throw new Error('Failed to load the conversation');
      return res.json();
    },
    // A row nobody opened is a row, not a fetch: bodies are live Gmail reads,
    // and a page of 25 threads would be 25 of them.
    enabled: expanded,
    // Bodies do not change. Re-reading them on focus spends quota to redisplay
    // text already on screen.
    staleTime: 5 * 60 * 1000,
  });

  const matched = thread.inquiry;
  const title = thread.subject ?? '(no subject)';
  // The vendor name is the identity worth reading; the address is the fallback
  // for a thread the matcher never tied to a vendor record.
  const identity = matched
    ? [matched.vendorName ?? thread.correspondent, matched.category].filter(Boolean).join(' · ')
    : thread.correspondent;

  // Phrasing content only: this block sits inside a button on one kind of row
  // and inside an anchor on the other, and neither may carry a div.
  const summary = (
    <span className="min-w-0 flex-1">
      <span className="flex items-baseline gap-2">
        <span className="truncate text-sm font-medium group-hover:underline">{title}</span>
        {/* No "no inquiry" marker on an unmatched row. The row already says so
            by how it behaves — a chevron that opens the thread in place, where a
            matched row carries the vendor and category and leaves for the
            inquiry. A badge naming what we *don't* hold describes our data model
            rather than the planner's mail, and it would sit on most rows in a
            wedding whose mail is mostly unmatched. */}
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
          {thread.messageCount} {thread.messageCount === 1 ? 'msg' : 'msgs'} ·{' '}
          {formatInstant(thread.lastMessageAt, { month: 'short', day: 'numeric' })}
        </span>
      </span>
      {identity && (
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">{identity}</span>
      )}
      {thread.snippet && !expanded && (
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {thread.snippet}
        </span>
      )}
    </span>
  );

  return (
    <Card className="overflow-hidden rounded-3xl bg-card p-0 shadow-ambient">
      {/* The rule under the header appears only when the thread is open, so a
          collapsed row stays a single quiet block. */}
      <div className={`flex items-start ${expanded ? 'border-b border-border' : ''}`}>
        {matched ? (
          <Link to={`/inquiries/${matched.id}`} className={HEADER_CONTROL}>
            {/* Three glyphs, one meaning each on this tab: this row goes
                somewhere, that row opens here, and the anchor beside both
                leaves for Gmail. */}
            <ArrowUpRight className="mt-0.5 size-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
            {summary}
          </Link>
        ) : (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={onToggle}
            className={HEADER_CONTROL}
          >
            {expanded ? (
              <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
            ) : (
              <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
            )}
            {summary}
          </button>
        )}

        {/* A sibling of the control, not a child: a link inside a button is
            neither reliably clickable nor announced as a link, and a link
            inside a link is not markup at all. */}
        {gmailAddress && (
          <a
            href={gmailThreadUrl(gmailAddress, thread.gmailThreadId)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open in Gmail: ${title}`}
            className="shrink-0 self-start py-4 pl-2 pr-5 text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            <ExternalLink className="size-4" />
          </a>
        )}
      </div>

      {expanded && (
        <div className="px-5 pb-5">
          {data ? (
            <div className="divide-y divide-border">
              {data.messages.map((message) => (
                <EmailMessage key={message.id} message={message} threadSubject={thread.subject} />
              ))}
            </div>
          ) : isLoading ? (
            <p className="py-4 text-sm text-muted-foreground">Loading this conversation…</p>
          ) : isError ? (
            // Scoped to this thread: every other row on the tab is still
            // readable, and re-reading one is cheaper than reloading the page.
            <p className="py-4 text-sm text-muted-foreground">
              This conversation couldn't be loaded from Gmail.{' '}
              <button
                type="button"
                onClick={() => refetch()}
                className="underline underline-offset-2"
              >
                Try again
              </button>
            </p>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/** A tab with no list in it: one card, one paragraph, and whatever there is to
 *  do about it. `text-muted-foreground` clears the contrast floor on this
 *  surface at 4.55:1; the filled container it would miss on is not used here. */
function Note({ children }: { children: ReactNode }) {
  return (
    <Card className="rounded-3xl bg-card p-5 shadow-ambient">
      <p className="text-sm text-muted-foreground">{children}</p>
    </Card>
  );
}
